import { Component, Input, Output, EventEmitter, signal, inject, DestroyRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { MessageService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { IdentityGroupsService, User as ApiUser } from '../../../service/identity-groups/identity-groups.service';

interface User {
  id: string | number;
  name: string;
  email: string;
  status: 'Active' | 'Inactive';
  username?: string;
  mobile?: string;
  enabled?: boolean;
}

@Component({
  selector: 'app-side-drawer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    ToastModule,
    AutoCompleteModule,
    TooltipModule
  ],
  template: `
    <!-- Side Drawer Overlay -->
    <div 
      *ngIf="visible" 
      class="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300"
      (click)="close()"
      [ngClass]="{'opacity-100': visible, 'opacity-0': !visible}"
    ></div>

    <!-- Side Drawer -->
    <div 
      class="fixed top-0 right-0 h-full w-[500px] bg-white dark:bg-zinc-900 shadow-2xl z-50 transition-transform duration-300 ease-in-out"
      [ngClass]="{'translate-x-0': visible, 'translate-x-full': !visible}"
    >
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-surface-200 dark:border-surface-700">
        <div>
          <h2 class="text-lg font-semibold text-surface-900 dark:text-zinc-100">{{ title }}</h2>
          <p class="text-sm text-surface-500 dark:text-surface-400">{{ subtitle }}</p>
        </div>
        <button 
          (click)="close()" 
          class="p-2 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg transition"
        >
          <i class="pi pi-times text-surface-500"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="p-4 h-[calc(100%-130px)] overflow-y-auto">
        <!-- Add User Button -->
        <div class="flex justify-end mb-4">
          <p-button
            label="Add User"
            icon="pi pi-plus"
            size="small"
            severity="primary"
            (onClick)="openAddUserDialog()"
          ></p-button>
        </div>

        <!-- Loading State -->
        <div *ngIf="isLoadingMembers()" class="flex justify-center items-center py-12">
          <i class="pi pi-spin pi-spinner text-2xl text-primary-500"></i>
        </div>

        <!-- Users List -->
        <div *ngIf="!isLoadingMembers() && users().length > 0; else emptyState" class="space-y-2">
          <div 
            *ngFor="let user of users()" 
            class="flex items-center justify-between p-3 bg-surface-50 dark:bg-surface-800 rounded-lg border border-surface-200 dark:border-surface-700 hover:border-primary-300 transition"
          >
            <div class="flex items-center gap-3">
              <!-- Avatar -->
              <div class="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                <span class="text-primary-700 dark:text-primary-300 font-semibold text-sm">
                  {{ getInitials(user.name) }}
                </span>
              </div>
              <!-- User Info -->
              <div>
                <p class="text-sm font-medium text-surface-900 dark:text-zinc-100">{{ user.name || user.username }}</p>
                <p class="text-xs text-surface-500 dark:text-surface-400">{{ user.email }}</p>
                <p *ngIf="user.mobile" class="text-xs text-surface-400">{{ user.mobile }}</p>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <!-- Status Badge -->
              <span 
                class="px-2 py-0.5 rounded-full text-xs font-medium"
                [ngClass]="{
                  'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300': user.status === 'Active' || user.enabled,
                  'bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300': user.status === 'Inactive' || !user.enabled
                }"
              >
                {{ user.status || (user.enabled ? 'Active' : 'Inactive') }}
              </span>
              <!-- Delete Button -->
              <button
                (click)="removeUser(user)"
                [disabled]="isRemovingMember()"
                class="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                pTooltip="Remove User"
                tooltipPosition="top"
              >
                <i class="pi pi-times"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <ng-template #emptyState>
          <div class="flex flex-col items-center justify-center h-64 text-center">
            <div class="w-16 h-16 rounded-full bg-surface-100 dark:bg-surface-800 flex items-center justify-center mb-4">
              <i class="pi pi-users text-2xl text-surface-400"></i>
            </div>
            <h3 class="text-sm font-medium text-surface-700 dark:text-surface-300">No users assigned</h3>
            <p class="text-xs text-surface-500 dark:text-surface-400 mt-1">Click "Add User" to assign members to this group</p>
          </div>
        </ng-template>
      </div>

      <!-- Footer -->
      <div class="absolute bottom-0 left-0 right-0 p-4 border-t border-surface-200 dark:border-surface-700 bg-white dark:bg-zinc-900">
        <div class="flex justify-between items-center">
          <span class="text-sm text-surface-500 dark:text-surface-400">
            {{ users().length }} member{{ users().length !== 1 ? 's' : '' }}
          </span>
          <p-button
            label="Close"
            severity="secondary"
            (onClick)="close()"
          ></p-button>
        </div>
      </div>
    </div>

    <!-- Add User Dialog -->
    <p-dialog
      [(visible)]="showAddUserDialog"
      [modal]="true"
      [closable]="true"
      [style]="{ width: '500px' }"
      header="Add User to Group"
      [draggable]="false"
      [resizable]="false"
    >
      <div class="space-y-4">
        <!-- AutoComplete Search -->
        <div>
          <label class="block text-sm font-medium mb-2">Search and Select User</label>
          <p-autocomplete
            [(ngModel)]="selectedUser"
            [suggestions]="filteredUsers()"
            (completeMethod)="searchUsers($event)"
            [dropdown]="true"
            dropdownMode="current"
            placeholder="Search by name or email..."
            optionLabel="name"
            inputStyleClass="w-full"
            class="w-full"
            [minLength]="1"
            [delay]="300"
            scrollHeight="200px"
            appendTo="body"
          >
            <ng-template let-user pTemplate="item">
              <div class="flex items-center gap-3 p-2">
                <div class="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                  <span class="text-primary-700 dark:text-primary-300 font-semibold text-xs">
                    {{ getInitials(user.name || user.username) }}
                  </span>
                </div>
                <div>
                  <p class="text-sm font-medium text-surface-900 dark:text-zinc-100">{{ user.name || user.username }}</p>
                  <p class="text-xs text-surface-500 dark:text-surface-400">{{ user.email }}</p>
                </div>
                <i *ngIf="isUserAlreadyAdded(user)" class="pi pi-check ml-auto text-green-500" pTooltip="Already in group"></i>
              </div>
            </ng-template>
          </p-autocomplete>
          <small class="text-surface-500 dark:text-surface-400 block mt-1">
            Type to search users or use the dropdown to browse available users
          </small>
        </div>

        <!-- Selected User Preview -->
        <div *ngIf="selectedUser" class="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
              <span class="text-primary-700 dark:text-primary-300 font-semibold text-sm">
                {{ getInitials(selectedUser.name || selectedUser.username) }}
              </span>
            </div>
            <div>
              <p class="text-sm font-medium text-surface-900 dark:text-zinc-100">{{ selectedUser.name || selectedUser.username }}</p>
              <p class="text-xs text-surface-500 dark:text-surface-400">{{ selectedUser.email }}</p>
            </div>
            <i *ngIf="isUserAlreadyAdded(selectedUser)" class="pi pi-check ml-auto text-green-500"></i>
          </div>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex justify-end gap-2">
          <p-button
            label="Cancel"
            severity="secondary"
            (onClick)="closeAddUserDialog()"
          ></p-button>
          <p-button
            label="Add User"
            severity="primary"
            [disabled]="!selectedUser || isUserAlreadyAdded(selectedUser) || isAddingUser()"
            [loading]="isAddingUser()"
            (onClick)="addSelectedUser()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class SideDrawerComponent implements OnInit {
  @Input() visible = false;
  @Input() title = 'Manage Members';
  @Input() subtitle = '';
  @Input() users = signal<User[]>([]);
  @Input() groupId!: number;
  
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() userAdded = new EventEmitter<User>();
  @Output() userRemoved = new EventEmitter<User>();

  private messageService = inject(MessageService);
  private identityGroupsService = inject(IdentityGroupsService);
  private destroyRef = inject(DestroyRef);

  showAddUserDialog = false;
  selectedUser: User | null = null;
  filteredUsers = signal<User[]>([]);
  
  isLoadingMembers = signal(false);
  isAddingUser = signal(false);
  isRemovingMember = signal(false);
  isSearchingUsers = signal(false);

  private searchSubject = new Subject<string>();

  ngOnInit() {
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((query) => {
        this.searchUsersFromApi(query);
      });
  }

  getInitials(name?: string): string {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  close() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.selectedUser = null;
    this.filteredUsers.set([]);
  }

  openAddUserDialog() {
    this.showAddUserDialog = true;
    this.selectedUser = null;
    this.filteredUsers.set([]);
    this.searchUsersFromApi('');
  }

  closeAddUserDialog() {
    this.showAddUserDialog = false;
    this.selectedUser = null;
    this.filteredUsers.set([]);
  }

  // AutoComplete search method
  searchUsers(event: any) {
    const query = event.query?.toLowerCase().trim() || '';
    this.searchSubject.next(query);
  }

  private searchUsersFromApi(query: string) {
    if (!this.groupId) return;
    
    this.isSearchingUsers.set(true);
    this.identityGroupsService.getAvailableUsers(this.groupId, query)
      .pipe(
        finalize(() => {
          this.isSearchingUsers.set(false);
        }),
        catchError((error) => {
          console.error('Failed to search users', error);
          return of([]);
        })
      )
      .subscribe({
        next: (apiUsers: ApiUser[]) => {
          const assignedUserIds = this.users().map(u => u.id?.toString()).filter(id => id);
          const available = apiUsers.filter(
            user => !assignedUserIds.includes(user.id.toString())
          );
          
          const uiUsers: User[] = available.map(user => ({
            id: user.id,
            name: user.name || user.username,
            email: user.email,
            username: user.username,
            mobile: user.mobile,
            enabled: user.enabled,
            status: user.enabled ? 'Active' : 'Inactive'
          }));
          
          this.filteredUsers.set(uiUsers);
        }
      });
  }

  isUserAlreadyAdded(user: User): boolean {
    if (!user || !user.id) return false;
    const userId = user.id.toString();
    return this.users().some(u => u && u.id && u.id.toString() === userId);
  }

  addSelectedUser() {
    if (!this.selectedUser || !this.groupId) return;
    
    if (this.isUserAlreadyAdded(this.selectedUser)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: `${this.selectedUser.name} is already in the group`
      });
      return;
    }

    this.isAddingUser.set(true);
    this.identityGroupsService.addMemberToGroup(this.groupId, Number(this.selectedUser.id))
      .pipe(
        finalize(() => {
          this.isAddingUser.set(false);
        }),
        catchError((error) => {
          console.error('Failed to add user', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to add ${this.selectedUser?.name} to the group`
          });
          return of(null);
        })
      )
      .subscribe({
        next: (addedUser: ApiUser | null) => {
          if (addedUser) {
            const uiUser: User = {
              id: addedUser.id,
              name: addedUser.name || addedUser.username,
              email: addedUser.email,
              username: addedUser.username,
              mobile: addedUser.mobile,
              enabled: addedUser.enabled,
              status: addedUser.enabled ? 'Active' : 'Inactive'
            };
            
            this.users.update(users => [...users, uiUser]);
            this.userAdded.emit(uiUser);
            
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `${uiUser.name} added to the group`
            });
            
            this.closeAddUserDialog();
          }
        }
      });
  }

  removeUser(user: User) {
    if (!this.groupId || !user || !user.id) return;
    
    this.isRemovingMember.set(true);
    this.identityGroupsService.removeMemberFromGroup(this.groupId, Number(user.id))
      .pipe(
        finalize(() => {
          this.isRemovingMember.set(false);
        }),
        catchError((error) => {
          console.error('Failed to remove user', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to remove ${user.name} from the group`
          });
          return of(null);
        })
      )
      .subscribe({
        next: () => {
          this.users.update(users => users.filter(u => u.id !== user.id));
          this.userRemoved.emit(user);
          this.messageService.add({
            severity: 'info',
            summary: 'Removed',
            detail: `${user.name} removed from the group`
          });
        }
      });
  }
}
